/**
 * Round B A14: an approval dialog opens on its own (an MCP request), so Radix has no trigger to hand focus back to and
 * leaves it on <body> when the dialog goes. This remembers what had focus when request `key` appeared — read during
 * render, before the dialog's focus scope moves focus to Deny — and puts it back once no request is shown. A queue of
 * requests keeps the first origin. The restore runs on the next tick, after Radix's own unmount focus.
 */
import { useEffect, useRef } from 'react';

export function useReturnFocus(key: string | null): void {
  const origin = useRef<{ key: string; element: HTMLElement | null } | null>(null);
  if (key && origin.current?.key !== key && typeof document !== 'undefined') {
    const active = document.activeElement;
    const element = origin.current?.element ?? (active instanceof HTMLElement && active !== document.body ? active : null);
    origin.current = { key, element };
  }
  useEffect(() => {
    if (key) return undefined;
    const element = origin.current?.element;
    origin.current = null;
    if (!element) return undefined;
    const timer = window.setTimeout(() => {
      const current = document.activeElement;
      if (element.isConnected && (!current || current === document.body)) element.focus();
    }, 0);
    return () => window.clearTimeout(timer);
  }, [key]);
}
